import React, { useState, useEffect } from 'react';
import { IAutoparkDriverWithProfileAndCars, IAutoparkCar } from '../../services/autopark.service';
import { CarService } from '../../services/car.service';
import { formatDateForApi } from '../../utils/date.utils';
import { formatCarNumber, formatCarNumberForBackend } from '../../utils/carNumberUtils';
import './AddAutoparkModal.css';
import './EditDriverModal.css';
import { formatPhone } from '../../utils/phone';
import { toBackendDate } from '../../utils/utils';
import { DRIVER_NOT_REGISTERED_MESSAGE } from '../../utils/driverRegistration';

interface EditDriverModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  driverData: IAutoparkDriverWithProfileAndCars;
  drivers: IAutoparkDriverWithProfileAndCars[];
  driverToEdit: string;
}

interface EditDriverFormData {
  firstName: string;
  lastName: string;
  phone: string;
  cars: {
    id?: string;
    number: string;
    expireDate: string;
  }[];
  newCarNumber: string;
  newCarExpireDate: string;
}

const EditDriverModal: React.FC<EditDriverModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  driverData,
  drivers,
  driverToEdit,
}) => {
  const [formData, setFormData] = useState<EditDriverFormData>({
    firstName: '',
    lastName: '',
    phone: '',
    cars: [],
    newCarNumber: '',
    newCarExpireDate: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const driver = drivers.find((driver) => driver.driver?.id === driverToEdit);
    if (driver && isOpen) {
      setFormData({
        firstName: driver.profile.firstName || '',
        lastName: driver.profile.lastName || '',
        phone: driver.profile.phone || '',
        cars: driver?.cars?.map((car) => ({
          id: car.id,
          number: car.number,
          expireDate: '2025-12-15', // Временно фиксированная дата
        })),
        newCarNumber: '',
        newCarExpireDate: '',
      });
      setError(null);
    }
  }, [driverToEdit, drivers, isOpen]);

  const handleInputChange = (field: keyof EditDriverFormData, value: string) => {
    if (field === 'newCarNumber') {
      // Применяем маску для номера автомобиля
      value = formatCarNumber(value);
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDeleteCar = async (index: number) => {
    const carToDelete = formData.cars[index];
    if (carToDelete.id) {
      try {
        setLoading(true);
        setError(null);

        // Вызываем API для удаления водителя из машины
        await CarService.removeDriverFromCar(carToDelete.id);

        // Удаляем машину из локального состояния
        setFormData((prev) => ({
          ...prev,
          cars: prev.cars.filter((_, i) => i !== index),
        }));
        onSuccess();
      } catch (error: any) {
        setError(error.message || 'Ошибка при удалении машины');
      } finally {
        setLoading(false);
      }
    } else {
      // Если машина новая (без ID), просто удаляем из состояния
      setFormData((prev) => ({
        ...prev,
        cars: prev.cars.filter((_, i) => i !== index),
      }));
    }
  };

  const handleAddCar = async () => {
    const driverID = driverData.driver?.id;
    if (!driverID) {
      setError(DRIVER_NOT_REGISTERED_MESSAGE);
      return;
    }
    if (formData.newCarNumber && formData.newCarExpireDate) {
      try {
        setLoading(true);
        setError(null);

        // Форматируем дату для API
        const formattedDate = toBackendDate(formData.newCarExpireDate);
        if (!formattedDate) {
          setError('Неверный формат даты');
          return;
        }

        // Получаем номер без пробелов для отправки на бэкенд
        const cleanCarNumber = formatCarNumberForBackend(formData.newCarNumber);

        // Вызываем API для обновления срока аренды машины
        await CarService.updateCarRentExpiration({
          number: cleanCarNumber,
          rentExpiredAt: formattedDate,
          driverID,
        });

        // Добавляем машину в локальное состояние
        setFormData((prev) => ({
          ...prev,
          cars: [
            ...prev.cars,
            {
              number: cleanCarNumber,
              expireDate: prev.newCarExpireDate,
            },
          ],
          newCarNumber: '',
          newCarExpireDate: '',
        }));
        onSuccess();
      } catch (error: any) {
        setError(error.message || 'Ошибка при добавлении машины');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !driverData) return null;

  return (
    <div className="autopark-modal-overlay" onClick={handleOverlayClick}>
      <button className="autopark-modal-close" onClick={onClose}>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <g clipPath="url(#clip0_162_26)">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12.0003 13.414L17.6573 19.071C17.8459 19.2532 18.0985 19.354 18.3607 19.3517C18.6229 19.3494 18.8737 19.2443 19.0591 19.0588C19.2445 18.8734 19.3497 18.6226 19.352 18.3604C19.3543 18.0982 19.2535 17.8456 19.0713 17.657L13.4143 12L19.0713 6.34303C19.2535 6.15443 19.3543 5.90182 19.352 5.63963C19.3497 5.37743 19.2445 5.12662 19.0591 4.94121C18.8737 4.7558 18.6229 4.65063 18.3607 4.64835C18.0985 4.64607 17.8459 4.74687 17.6573 4.92903L12.0003 10.586L6.34331 4.92903C6.15386 4.75137 5.90272 4.65439 5.64304 4.65861C5.38336 4.66283 5.1355 4.76791 4.95192 4.95162C4.76834 5.13533 4.66343 5.38326 4.6594 5.64295C4.65537 5.90263 4.75252 6.1537 4.93031 6.34303L10.5863 12L4.92931 17.657C4.8338 17.7493 4.75762 17.8596 4.70521 17.9816C4.6528 18.1036 4.62522 18.2348 4.62406 18.3676C4.62291 18.5004 4.64821 18.6321 4.69849 18.755C4.74877 18.8779 4.82302 18.9895 4.91692 19.0834C5.01081 19.1773 5.12246 19.2516 5.24536 19.3018C5.36825 19.3521 5.49993 19.3774 5.63271 19.3763C5.76549 19.3751 5.89671 19.3475 6.01872 19.2951C6.14072 19.2427 6.25106 19.1665 6.34331 19.071L12.0003 13.414Z"
              fill="#3563E9"
            />
          </g>
          <defs>
            <clipPath id="clip0_162_26">
              <rect width="24" height="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </button>
      <div className="autopark-modal-content">
        <div className="autopark-modal-header">
          <h2 className="autopark-modal-title">Внести изменения в карточку водителя</h2>
        </div>

        <div className="autopark-modal-body">
          {/* ФИО */}

          <div className="edit-driver-modal-row">
            <label className="edit-driver-modal-label">ФИО:</label>
            <span className="edit-driver-modal-value">
              {formData.firstName} {formData.lastName}
            </span>
          </div>

          {/* Номер телефона */}
          <div className="edit-driver-modal-row">
            <label className="edit-driver-modal-label">Номер телефона:</label>
            <span className="edit-driver-modal-value">{formatPhone(formData.phone)}</span>
          </div>

          {formData?.cars?.map((car, index) => (
            <div className="edit-driver-modal-row">
              <div className="edit-driver-modal-row-number">
                <label className="edit-driver-modal-label"> {index + 1}. Гос номер авто: </label>
                <span className="edit-driver-modal-value">{car.number}</span>
              </div>

              <div className="edit-driver-modal-row-expire">
                <label className="edit-driver-modal-label">Срок до:</label>
                <span className="edit-driver-modal-value">{car.expireDate}</span>
              </div>

              <div className="edit-driver-modal-row-delete">
                <button
                  className="edit-driver-modal-delete-button"
                  onClick={() => handleDeleteCar(index)}
                  disabled={loading}
                >
                  Удалить
                </button>
              </div>
            </div>
          ))}

          {/* Добавление нового автомобиля */}
          <div className="edit-driver-modal-row-add-car">
            <div className="autopark-form-group">
              <label className="autopark-form-label">Добавить гос номер:</label>
              <input
                type="text"
                className="autopark-form-input"
                value={formData.newCarNumber}
                onChange={(e) => handleInputChange('newCarNumber', e.target.value)}
                placeholder="А 111 АА 111"
                maxLength={12}
              />
            </div>
            <div className="autopark-form-group">
              <label className="autopark-form-label">Добавить срок действия:</label>
              <input
                type="date"
                className="autopark-form-input"
                value={formData.newCarExpireDate}
                onChange={(e) => handleInputChange('newCarExpireDate', e.target.value)}
              />
            </div>

            <button
              type="button"
              onClick={handleAddCar}
              style={{
                background: 'none',
                border: 'none',
                color: '#1D61E7',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '14px',
                marginBottom: '8px',
                alignSelf: 'end',
                marginLeft: '20px',
              }}
              disabled={!formData.newCarNumber || !formData.newCarExpireDate}
            >
              Добавить
            </button>
          </div>

          {/* Кнопка сохранить */}
          {/* <div className="autopark-form-row" style={{ marginTop: '32px', justifyContent: 'flex-start' }}>
            <button
              className="edit-driver-modal-save-button"
              onClick={handleSave}
              disabled={loading}
              style={{ width: 'auto', padding: '12px 24px' }}
            >
              {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div> */}
        </div>

        {error && <div className="autopark-modal-error">{error}</div>}
      </div>
    </div>
  );
};

export default EditDriverModal;
